import type { LevelDef, PlanetDef, RegionDef } from '@playnets/sim';
import { useUi, type Screen } from '../store';

/**
 * The poster margin (§10.4). A portrait game on a 16:9 monitor leaves two big
 * empty columns; the answer isn't to stretch, reflow, or drop-shadow a phone
 * mockup — it's to treat the playfield as a printed plate and the surround as
 * the caption block on a museum specimen card.
 *
 * A specimen card is a name, a designation and one line. It is not an essay,
 * and neither is this: the margin holds the placard and the planet's voice, and
 * everything else was cut.
 */
export default function Margins({
  side,
  planet,
  level,
  screen,
  region,
}: {
  side: 'left' | 'right';
  planet: PlanetDef;
  level: LevelDef;
  screen: Screen;
  region: RegionDef;
}): JSX.Element {
  const say = useUi((s) => s.say);

  if (side === 'left') {
    return (
      <aside className="margin margin--left" aria-hidden="true">
        <p className="placard-eyebrow">{planet.designation}</p>
        <h1 className="placard-title">{planet.name}</h1>
        <p className="placard-body">{planet.epithet}</p>
        <div className="placard-rule" />
        <div className="placard-row">
          <span>{planet.passive.label}</span>
        </div>
        <p className="placard-body">{planet.passive.text}</p>
      </aside>
    );
  }

  return (
    <aside className="margin margin--right" aria-hidden="true">
      <p className="placard-eyebrow">{region.name}</p>
      <h1 className="placard-title">{level.name}</h1>
      <div className="placard-rule" />
      {screen === 'battle' ? (
        <p className="placard-say">{say}</p>
      ) : (
        <p className="placard-body">{region.subtitle}</p>
      )}
    </aside>
  );
}
